import { getAllDSPs, getBrandNames, getContentPartners, getCreativesV2 } from '../../services/creativeTrackerV2';
import {
  ADOMAIN_KEY,
  ASSET_ID_KEY,
  ASSET_NAME_KEY,
  BRAND_NAME_KEY,
  CREATED_AT_KEY,
  PREVIEW_KEY,
  REVIEW_STATUS_KEY,
  UPDATED_AT_KEY,
} from '../../pages/CreativeTrackerV2/constants';
import clone from 'clone-deep';
import { formatCpOptions } from '../creativeTracker/constants';
import { currentUserSelector } from '../app';
import { ACCESS_CONTENT_PARTNER, ACCESS_DSP, ALL } from '../constants';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import { FORM_UUID_RULE, FORM_UUID_LIST_RULE } from '../../utils';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { v4 as uuidv4 } from 'uuid';
import { DSP_PROVIDER } from './constants';

dayjs.extend(utc);

export const getCountryOptions = () => {
  countries.registerLocale(enLocale); // Ensure locale is registered
  return Object.entries(countries.getNames('en', { select: 'official' })).map(([code, name]) => ({
    value: code,
    label: name,
  }));
};

let latestBrandSearch = '';

const INITIAL_FILTER_STATE = {
  'review-statuses': {
    name: 'Review Statuses',
    type: 'multiselect',
    selected: [],
    options: [
      { label: 'Accepted', value: 'ACCEPTED' },
      { label: 'Pending', value: 'PENDING' },
      { label: 'Declined', value: 'DECLINED' },
    ],
    rules: [],
  },
  'last-updated-range': {
    name: 'Last Updated Date Range',
    type: 'date',
    selected: [dayjs().startOf('day').subtract(13, 'day'), dayjs().endOf('day')],
    options: [],
    rules: [],
  },
  'ad-id': {
    name: 'Ad ID',
    type: 'input',
    selected: null,
    rules: [FORM_UUID_RULE],
  },
  'creative-ids': {
    name: 'Creative IDs',
    type: 'multivalue-input',
    selected: [],
    rules: [FORM_UUID_LIST_RULE],
  },
  'source-creative-ids': {
    name: 'Source Creative IDs',
    type: 'multivalue-input',
    selected: [],
  },
  'source-ad-ids': {
    name: 'Source Ad IDs',
    type: 'multivalue-input',
    selected: [],
    options: [],
  },
  'asset-ids': {
    name: 'Asset IDs',
    type: 'multivalue-input',
    selected: [],
    options: [],
    rules: [FORM_UUID_LIST_RULE],
  },
  adomains: {
    name: 'Adomains',
    type: 'multivalue-input',
    selected: [],
    rules: [],
  },
  'brand-crm-ids': {
    name: 'Brands',
    type: 'autocomplete',
    selected: [],
  },
  'media-url': {
    name: 'Media File URL',
    type: 'input',
    selected: null,
    rules: [],
  },
  'content-partner-ids': {
    name: 'Content Partner',
    type: 'multiselect',
    selected: [],
    options: [],
    rules: [],
  },
  'dsp-ids': {
    name: 'DSPs',
    type: 'multiselect',
    selected: [],
    options: [],
  },
  'asset-source-sub-classes': {
    name: 'Subclasses',
    type: 'multiselect',
    selected: [],
    options: [
      { label: 'Programmatic', value: 'ProgrammaticAssetSourceDto' },
      { label: 'Partner', value: 'PartnerAssetSourceDto' },
      { label: 'Creative', value: 'CreativeAssetSourceDto' },
    ],
  },
  'asset-source-external-partners': {
    name: 'External Partners',
    type: 'multiselect',
    selected: [],
    options: [
      { label: 'Google', value: 'GOOGLE' },
      { label: 'FreeWheel', value: 'FREEWHEEL' },
    ],
  },
  'tracking-ids': {
    name: 'Tracking IDs',
    type: 'multivalue-input',
    selected: [],
    options: [],
    rules: [],
  },
  timings: {
    name: 'Ingestion Type',
    type: 'multiselect',
    selected: [],
    options: [
      { label: 'PRE_INGEST', value: 'PRE_INGEST' },
      { label: 'DYNAMIC', value: 'DYNAMIC' },
    ],
  },
  countries: {
    name: 'Countries',
    type: 'multiselect',
    selected: [],
    options: getCountryOptions(),
  },
  'campaign-manager-email': {
    name: 'Campaign Manager Email',
    type: 'input',
    selected: null,
    rules: [],
  },
};

export const v2filterSelectedSelector = (state) => {
  const filterSelected = state.creativeTrackerV2.filters;
  return filterSelected;
};

const filterContentPartners = (contentPartners, abilities) =>
  contentPartners.filter(
    (partner) =>
      abilities.can(ACCESS_CONTENT_PARTNER, ALL) || abilities.can(ACCESS_CONTENT_PARTNER, partner.name.toLowerCase())
  );

const filterDsps = (dsps, abilities) => {
  return dsps.filter((dsp) => abilities.can(ACCESS_DSP, ALL) || abilities.can(ACCESS_DSP, dsp[DSP_PROVIDER.DSP_UUID]));
};

export const formatDspOptions = (dsps) => {
  return dsps.map((dsp) => {
    return {
      label: dsp[DSP_PROVIDER.DSP_NAME],
      value: dsp[DSP_PROVIDER.DSP_UUID],
    };
  });
};

const updateCreatives = (creatives) => ({
  type: 'updateCreatives',
  payload: (creatives || []).map((creative) => ({
    ...creative,
    key: uuidv4(),
  })),
});

const parseFilterVals = (filterSelected) => {
  const parsedFilters = {};
  Object.entries(filterSelected).forEach(([key, filter]) => {
    if (filter?.type === 'date') {
      if (key === 'last-updated-range') {
        const [after, before] = filter.selected || [];
        parsedFilters['updated-at-after'] = after ? after.utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : undefined;
        parsedFilters['updated-at-before'] = before ? before.utc().format('YYYY-MM-DDTHH:mm:ss[Z]') : undefined;
      }
    } else if (filter?.type === 'input') {
      parsedFilters[key] = typeof filter.selected === 'string' ? filter.selected : null;
    } else if (filter?.type === 'select') {
      parsedFilters[key] = Array.isArray(filter.selected) ? filter.selected[0] : null;
    } else {
      parsedFilters[key] = filter.selected || [];
    }
  });
  return parsedFilters;
};

export const creativeTrackerV2 = {
  namespace: 'creativeTrackerV2',
  state: {
    creatives: [],
    creativeWidths: {
      [ASSET_NAME_KEY]: 50,
      [REVIEW_STATUS_KEY]: 50,
      [PREVIEW_KEY]: 50,
      [BRAND_NAME_KEY]: 75,
      [CREATED_AT_KEY]: 75,
      [UPDATED_AT_KEY]: 75,
      [ASSET_ID_KEY]: 100,
      [ADOMAIN_KEY]: 100,
    },
    filters: INITIAL_FILTER_STATE,
  },
  reducers: {
    updateCreatives(state, { payload: creatives }) {
      return { ...state, creatives };
    },
    updateCreativeWidths(state, { payload: creativeWidths }) {
      return { ...state, creativeWidths };
    },
    updateFilterSelected(state, { payload: selected }) {
      const currState = clone(state);
      Object.entries(selected).forEach(([field, values]) => {
        if (!currState.filters[field]) {
          currState.filters[field] = {};
        }
        currState.filters[field].selected = values;
      });
      return currState;
    },
    updateCpIdOptions(state, { payload: { cps, abilities } }) {
      const currState = clone(state);
      currState.filters['content-partner-ids'].options = formatCpOptions(filterContentPartners(cps, abilities));
      return currState;
    },
    updateDspOptions(state, { payload: { dsps, abilities } }) {
      const currState = clone(state);
      currState.filters['dsp-ids'].options = formatDspOptions(filterDsps(dsps, abilities));
      return currState;
    },
    updateBrandIdOptions(state, { payload: { brands } }) {
      const currState = clone(state);
      const brandOptions = (brands || []).map((brand) => ({
        label: brand['name'],
        value: brand['id'],
      }));
      currState.filters['brand-crm-ids'].options = brandOptions;
      return currState;
    },
    clearBrandOptions(state) {
      const currState = clone(state);
      currState.filters['brand-crm-ids'].options = [];
      return currState;
    },
  },
  effects: {
    *pageInit(emptyPayload, { call, put, select }) {
      // load in cp identifiers
      const { permissions: abilities } = yield select(currentUserSelector);
      const cpResponse = yield call(getContentPartners);
      if (cpResponse?.data) {
        yield put({ type: 'updateCpIdOptions', payload: { cps: cpResponse.data, abilities } });
      }

      const dspResponse = yield call(getAllDSPs);
      const dspInfoList = dspResponse?.data?.['dsp-info-list'] ?? [];
      yield put({
        type: 'updateDspOptions',
        payload: { dsps: dspInfoList, abilities },
      });

      const creatives = yield call(getCreativesV2, parseFilterVals(INITIAL_FILTER_STATE));
      yield put(updateCreatives(creatives?.data?.['search-data']) || []);
    },
    *pageUpdate(emptyPayload, { call, put, select }) {
      const filterSelected = yield select(v2filterSelectedSelector);
      const creatives = yield call(
        getCreativesV2,
        parseFilterVals(filterSelected) || parseFilterVals(INITIAL_FILTER_STATE)
      );
      yield put(updateCreatives(creatives?.data?.['search-data']) || []);
    },
    *updateBrandOptions({ payload: enteredStr }, { call, put }) {
      latestBrandSearch = enteredStr; // Track the latest search
      if (!enteredStr) {
        yield put({ type: 'updateBrandIdOptions', payload: { brands: [] } });
        return;
      }
      const brandNamesResponse = yield call(getBrandNames, { label: enteredStr || '' });
      if (enteredStr === latestBrandSearch && brandNamesResponse?.data) {
        const brands = brandNamesResponse.data;
        const limitedBrands = brands.length > 100 ? brands.slice(0, 100) : brands;
        yield put({ type: 'updateBrandIdOptions', payload: { brands: limitedBrands } });
      }
    },
  },
};
